<template>
  <menu-popover
    v-model:is-menu-opened="isMenuOpen"
    :menu-items="transformedMenuItems"
    :menu-name="$T('Context Menu')"
    :trigger-rect="triggerRect"
    :is-show-scroll-indicator="true"
    :min-popover-height="220"
    :offset-x="0"
    :offset-y="0"
    position="bottom-left"
    @close="onMenuClose"
  >
    <template #item-right="{ item }">
      <span
        v-if="item.meta?.shortcut && !isPhoneSizeViewportWidth"
        class="pointer-events-none whitespace-nowrap pl-1 pr-2 text-desktop-body-medium text-text-tertiary-light dark:text-text-tertiary-dark"
        >{{ ShortcutManager.normalizeShortcutText(item.meta.shortcut) }}</span
      >
    </template>
  </menu-popover>
</template>

<script lang="ts">
export default {
  name: 'EmbedEditorContextMenu',
}
</script>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { ShortcutManager } from 'client/library/shortcut-manager'
import { useServices } from 'app/fragments/use-services'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { useContextMenu as useEmbedContextMenu } from './use-context-menu'
import { buildTextMenu } from 'client/pages/app/fragments/compositions/ui/context-menu/text-menu'
import type { MenuItem } from 'client/components/types'
import type { FrameMenuItem } from 'app/types'
import type { Rect } from 'utils/metrics'
import MenuPopover from 'client/components/composite/menu-popover.vue'
import { useWindowResize } from 'client/composables/window-resize'
import { useGlobalEvents } from 'client/store/global-events'
import { buildNoteMenu } from './embed-editor-note-menu'

const { isPhoneSizeViewportWidth } = useWindowResize()
const services = useServices()
const { workbook } = storeToRefs(useWorkspaceStore())

const showContextMenu = (point: { x: number; y: number }) => {
  if (!menuItems.value?.length) return
  const editorRect = useWorkspaceStore().editorRect ?? { x: 0, y: 0, width: 0, height: 0 }
  // Create a virtual trigger rect  at the click position
  triggerRect.value = { x: point.x + editorRect.x, y: point.y + editorRect.y, width: 0, height: 0 }
  isMenuOpen.value = true
}

const { menuItems } = useEmbedContextMenu({
  services,
  popMenu: showContextMenu,
})

const isMenuOpen = ref(false)
const triggerRect = ref<Rect | null>(null)

const transformedMenuItems = computed(() => {
  if (!menuItems.value) return []

  const toMenuItem = (item: FrameMenuItem) => {
    const proLockObject = {
      iconName: 'lock-pro',
      iconSize: isPhoneSizeViewportWidth.value ? 20 : 14,
      iconClass: !isPhoneSizeViewportWidth.value && 'ml-1',
    }

    return {
      title: item.label || '',
      iconName: item.iconName,
      action: item.action as () => void | Promise<void>,
      disabled: !item.enable,
      ...(item.isPro ? proLockObject : {}),
      meta: {
        shortcut: item.shortcut,
        sourceMenuItem: item,
      },
    } as MenuItem
  }

  return menuItems.value.map((item: FrameMenuItem): MenuItem => {
    if (item.type === 'separator') {
      return { divider: true }
    }

    return {
      ...toMenuItem(item),
      subMenuItems: item.subMenuItems?.map((subItem: FrameMenuItem): MenuItem => {
        if (subItem.type === 'separator') {
          return { divider: true }
        }
        return toMenuItem(subItem)
      }),
    }
  })
})

const showNoteContextMenu = (point: { x: number; y: number }) => {
  menuItems.value = buildNoteMenu(services)
  triggerRect.value = { x: point.x, y: point.y, width: 0, height: 0 }
  isMenuOpen.value = true
}

const onMenuClose = () => {
  isMenuOpen.value = false
}

const handleContextmenu = (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
  const context = workbook.value.state.contexts
  const isInEditTitleMode = context.isInEditTitleMode
  const target = e.target as HTMLElement
  const isEditableElement = target.closest('textarea, input[type="text"], [contenteditable="true"], [contenteditable=""]')
  if (isInEditTitleMode && isEditableElement) {
    menuItems.value = buildTextMenu(services)
    showContextMenu({ x: e.clientX, y: e.clientY })
  }
}

watch(isMenuOpen, newValue => {
  if (newValue && isPhoneSizeViewportWidth.value) {
    useGlobalEvents().trigger<{ height: number }>('centerSelectionForBottomDrawer', { height: 420 })
  }
})

onMounted(() => {
  workbook.value?.container?.addEventListener('contextmenu', handleContextmenu, { capture: true })
})
onBeforeUnmount(() => {
  workbook.value?.container?.removeEventListener('contextmenu', handleContextmenu, { capture: true })
})

defineExpose({
  showNoteContextMenu,
})
</script>
